import {castArray} from 'lodash';
import {HoistAuthModel, IdentityInfo, XH} from '@xh/hoist/core';
import {UserValidationInfo} from './DataTypes';
import {forgotPasswordLink, setPasswordFromLinkAsync, takeSetPasswordToken, TextInputFactory} from './PasswordLinks';
import {img} from '@xh/hoist/cmp/layout';
import './LoginPanel.scss';

export class RzxAuthModel extends HoistAuthModel {
    /** The platform's TextInput, for the set-password dialog - set by each app's entry point. */
    static textInput: TextInputFactory;

    private svcUrl: string;
    idToken: string;
    roles: string[];
    email: string;
    name: string;
    /** True for a shadow user (read-only), as rzx-backend reports it for the token in use. */
    isShadow = false;

    constructor() {
        super();

        if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
            this.svcUrl = `${window.location.protocol}//${window.location.hostname}:5000/csapi`;
        } else {
            this.svcUrl = `${window.location.protocol}//${window.location.hostname}/csapi`;
        }
    }

    hasRole(role: string): boolean {
        return this.roles.includes(role);
    }

    override async getAuthStatusFromServerAsync(): Promise<IdentityInfo> {
        try {
            const {authenticated, identity} = await XH.fetchJson({url: 'xh/authStatus'});
            return authenticated ? this.parseIdentityInfo(identity) : null;
        } catch (e) {
            console.error('Error fetching auth status from server', e);
            return null;
        }
    }

    override async completeAuthAsync(): Promise<IdentityInfo> {
        // Opened from an emailed set-password link: once set, carry on signed in with the token it answers with
        const setPasswordToken = takeSetPasswordToken();
        if (setPasswordToken) {
            const idToken = await setPasswordFromLinkAsync(this.svcUrl, setPasswordToken, RzxAuthModel.textInput);
            if (idToken) localStorage.setItem('jwtToken', idToken);
        }

        const jwtToken = localStorage.getItem('jwtToken');
        if (jwtToken) {
            this.idToken = jwtToken;
            this.installDefaultFetchServiceHeaders();

            try {
                const userInfo = await this.validateTokenAsync();

                this.extractTokenFields();

                // Keep an existing server session for this token's user - e.g. an impersonation, which
                // reloads the app. xh/login would replace it with the user's own, ending the impersonation.
                let identity = await this.getAuthStatusFromServerAsync();
                if (identity?.authUser?.username?.toLowerCase() !== userInfo.userName?.toLowerCase()) {
                    await XH.fetchJson({
                        url: 'xh/login',
                        params: {username: userInfo.userName, password: '*****'}
                    });
                    identity = await this.getAuthStatusFromServerAsync();
                }

                // While Hoist shows an impersonation, rzx-backend must act as the impersonated user too.
                if (identity && identity.apparentUser.username !== identity.authUser.username) {
                    identity = await this.useImpersonationTokenAsync(identity);
                }
                return identity;
            } catch (e) {
                console.error(e);
            }
        }

        // Prefs are not loaded before login, so the page would be light. Show the login form dark, over the
        // dark background image - not persisted, and replaced by the user's own theme once logged in.
        XH.setTheme('dark', false);

        XH.appSpec.enableLoginForm = true;
        XH.appSpec.loginPanelIcon = img({src: '/public/favicon-512.png', height: 48});
        // The login panel renders this message as content, so it can be a link - AppSpec types it as a string
        XH.appSpec.loginMessage = forgotPasswordLink(this.svcUrl) as unknown as string;
        return null;
    }

    override async loginWithCredentialsAsync(username: string, password: string): Promise<IdentityInfo> {
        let authResponse: any;

        try {
            authResponse = await XH.fetchService.postJson({
                url: this.svcUrl + '/login/authenticate',
                body: {username, password},
                headers: {'Content-Type': 'application/json'}
            });
        } catch (e) {
            // The login panel says "Login incorrect", and keeps its "Forgot your password?" link
            if (e.name == 'Unauthorized') return null;
            throw e;
        }

        this.idToken = authResponse.token;
        this.extractTokenFields();
        console.log('c# backend authenticated user');
        this.installDefaultFetchServiceHeaders();
        localStorage.setItem('jwtToken', this.idToken);

        await XH.fetchJson({
            url: 'xh/login',
            params: {username, password}
        });

        await this.validateTokenAsync();

        var ret = await this.getAuthStatusFromServerAsync();
        return ret;
    }

    override async logoutAsync() {
        await super.logoutAsync();
        localStorage.removeItem('jwtToken');
        try {
            await XH.fetchService.postJson({
                url: this.svcUrl + '/login/logout',
                headers: {'Content-Type': 'application/json'}
            });
        } catch (e) {
            XH.dangerToast(e.message);
        }
    }

    /**
     * Switch to a token for the impersonated user, issued by rzx-backend under Hoist's impersonation
     * rules. The real user's token stays in localStorage, so ending the impersonation (which reloads
     * the app) returns to it. If rzx-backend refuses, end the Hoist impersonation too, so the two agree.
     */
    private async useImpersonationTokenAsync(identity: IdentityInfo): Promise<IdentityInfo> {
        try {
            const {token} = await XH.fetchService.postJson({
                url: this.svcUrl + '/login/impersonate',
                body: {username: identity.apparentUser.username},
                headers: {'Content-Type': 'application/json'}
            });
            this.idToken = token;
            this.extractTokenFields();
            await this.validateTokenAsync();
            return identity;
        } catch (e) {
            console.error('Impersonation token refused - ending impersonation', e);
            await XH.fetchJson({url: 'xh/endImpersonate'});
            return this.getAuthStatusFromServerAsync();
        }
    }

    /** Checks the token in use with rzx-backend, and notes whether its user is a shadow user. */
    private async validateTokenAsync(): Promise<UserValidationInfo> {
        const userInfo = (await XH.fetchService.postJson({
            url: this.svcUrl + '/login/validateToken',
            headers: {'Content-Type': 'application/json'}
        })) as UserValidationInfo;
        this.isShadow = !!userInfo.isShadow;
        return userInfo;
    }

    private installDefaultFetchServiceHeaders() {
        XH.fetchService.addDefaultHeaders(opts => {
            return {Authorization: `Bearer ${this.idToken}`};
        });
    }

    private extractTokenFields() {
        const parts = this.idToken.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid JWT format');
        }

        const decodedPayload = JSON.parse(atob(parts[1]));
        this.name = decodedPayload.unique_name;
        this.email = decodedPayload.email;
        this.roles = castArray(decodedPayload.role ?? []);
    }
}
